import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';
import { Keyboard, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { useTransition } from 'transition-hook';

import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { Text } from '@/components/shared/element/text';
import { Container } from '@/components/shared/layout/container';
import { UspContent } from '@/components/shared/my24/usp-content';
import {
  PaginationBar,
  PaginationBarProps,
} from '@/components/shared/navigation/pagination-bar';
import {
  disableAllSwiperKeyboard,
  enableAllSwiperKeyboard,
} from '@/components/shared/utility/swiper';
import { styled } from '@/stitches.config';
import { IItem, IMy24Theme, IMy24Tracking } from '@/types/shared';

const Modal = dynamic(() => import('@/components/shared/overlays/modal'), {
  ssr: false,
});

const BeginState = {
  opacity: 0,
  transform: 'translate(0, 30px) !important',
};

const EndState = {
  opacity: 1,
  transform: 'translate(0,0) !important',
  transitionProperty: 'opacity, transform',
  transitionDuration: '0.6s',
  transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
};

const transitionDelays = [0.6, 0.8, 1, 1.2, 1.4, 1.6];

const StyledUspSlider = styled('div', {
  position: 'relative',
  '& .swiper': {
    overflow: 'initial',
  },
  '& .button-navigation-prev, & .button-navigation-next': {
    position: 'absolute',
    top: '135px',
    zIndex: 2,
    '@maxlg': {
      display: 'none',
    },
    '&.swiper-button-disabled': {
      opacity: '0',
    },
  },
  '& .button-navigation-prev': {
    left: '$space-12',
    '@xl': {
      left: '$space-16',
    },
  },
  '& .button-navigation-next': {
    right: '$space-12',
    '@xl': {
      right: '$space-16',
    },
  },
  variants: {
    show: {
      false: {
        '.swiper-slide': {
          ...BeginState,
        },
      },
      true: {
        '.swiper-slide': {
          ...EndState,
          ...transitionDelays.reduce((acc, delay, index) => {
            acc[`&:nth-child(${index + 1})`] = {
              transitionDelay: `${delay}s`,
            };
            return acc;
          }, {}),
        },
      },
    },
    skew: {
      true: {
        '& .swiper-slide': {
          width: 'calc(100% - ((32px + 32px) - 48px))',
          '@xs': {
            width: 'calc(100% - ((48px + 32px) - 48px))',
          },
          '@sm': {
            width: 'calc(100% - ((96px + 48px) - 96px))',
          },
          '@md': {
            width: 'calc(100% - ((96px + 64px) - 96px))',
          },
          '@lg': {
            width: 488,
            pe: 'none',
          },
        },
      },
      false: {
        '& .swiper-slide': {
          width: 'calc(100% - 16px)',
          '@xs': {
            width: 'calc(100% - ((48px + 32px) - 48px))',
          },
          '@sm': {
            width: 'calc(100% - ((128px + 128px) - 96px))',
          },
          '@md': {
            width: 'calc(100% - ((156px + 156px) - 96px))',
          },
          '@lg': {
            width: 384,
            pe: 'none',
          },
        },
      },
    },
  },
  defaultVariants: {
    skew: true,
  },
});

interface ProductUspSliderProps {
  items?: IItem[];
  show?: boolean;
  hash?: string;
  remark?: string;
  theme?: IMy24Theme;
  pagination?: PaginationBarProps;
  dataTrack?: IMy24Tracking;
}

export const ProductUspSlider: React.FC<ProductUspSliderProps> = ({
  items,
  show,
  hash,
  remark,
  dataTrack,
  theme = 'light',
  pagination = {},
}) => {
  const [modalProduct, setModalProduct] = useState<{
    data: { index: number } | null;
    isOpen: boolean;
  }>({
    data: null,
    isOpen: false,
  });

  const TOGGLE_MODAL_TRANS = useTransition(modalProduct.isOpen, 500);

  const onOpenModal = (index: number) => {
    setModalProduct({ isOpen: true, data: { index } });
    disableAllSwiperKeyboard();
  };
  const onCloseModal = () => {
    setModalProduct({ isOpen: false, data: null });
    enableAllSwiperKeyboard();
  };

  const [swiper, setSwiper] = useState<SwiperClass | null>(null);

  useEffect(() => {
    if (!swiper || !show) return;
    swiper.slideTo(0, 0);
  }, [show, swiper]);

  const paginationCss = useMemo(
    () => ({
      marginTop: '$space-10',
      '@lg': {
        marginTop: '$space-14',
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        display: items?.length > 2 ? 'block' : 'none',
      },
      '@4xl': {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        display: items?.length > 3 ? 'block' : 'none',
      },
    }),
    [items]
  );

  const navigationCss = useMemo(
    () => ({
      '@4xl': {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        display: items?.length > 3 ? 'block' : 'none',
      },
    }),
    [items]
  );

  return (
    <>
      <StyledUspSlider show={show}>
        <Container maxWidth="2xl">
          <Swiper
            modules={[Keyboard, Navigation, Pagination]}
            keyboard
            speed={1000}
            slidesPerView={'auto'}
            spaceBetween={16}
            threshold={10}
            slidesOffsetBefore={0}
            slidesOffsetAfter={0}
            centeredSlides
            centeredSlidesBounds
            allowTouchMove
            onSwiper={setSwiper}
            navigation={{
              prevEl: `.button-product-${hash}-prev`,
              nextEl: `.button-product-${hash}-next`,
            }}
            pagination={{
              el: `.swiper-pagination-product-${hash}`,
              clickable: true,
              renderBullet: function (index, className) {
                return `<div class="${className}" data-test="pagination_${
                  index + 1
                }"><span>${index + 1}</span></div>`;
              },
            }}
            breakpoints={{
              '640': {
                slidesOffsetBefore: 0,
                slidesOffsetAfter: 0,
                spaceBetween: 24,
                centeredSlides: true,
                centeredSlidesBounds: true,
                allowTouchMove: true,
              },
              '768': {
                slidesOffsetBefore: 0,
                slidesOffsetAfter: 0,
                spaceBetween: 32,
                centeredSlides: true,
                centeredSlidesBounds: true,
                allowTouchMove: true,
              },
              '1024': {
                slidesOffsetBefore: 0,
                slidesOffsetAfter: 125,
                spaceBetween: 32,
                centeredSlides: false,
                centeredSlidesBounds: false,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                allowTouchMove: items?.length > 2,
              },
              '1920': {
                slidesOffsetBefore: -125,
                slidesOffsetAfter: 125,
                spaceBetween: 32,
                centeredSlides: false,
                centeredSlidesBounds: false,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                allowTouchMove: items?.length > 3,
              },
              '2560': {
                slidesOffsetBefore: -125,
                slidesOffsetAfter: 125,
                spaceBetween: 32,
                centeredSlides: false,
                centeredSlidesBounds: false,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                allowTouchMove: items?.length > 3,
              },
            }}
            data-test={hash + `_items`}
          >
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            {items?.length > 0 &&
              items?.slice(0, 9).map((item, i) => (
                <SwiperSlide
                  key={i}
                  data-test={hash + `_item_${i + 1}`}
                  data-track={dataTrack}
                  data-track-section={hash}
                  data-track-value={`slide_${i + 1}`}
                >
                  <UspContent
                    theme={theme}
                    item={item}
                    onClick={() => onOpenModal(i)}
                    open={modalProduct.isOpen}
                    dataTest="content"
                  />
                </SwiperSlide>
              ))}
          </Swiper>
        </Container>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        {items?.length > 2 && (
          <>
            <ButtonIcon
              className={`button-navigation-prev button-product-${hash}-prev`}
              label="Previous"
              icon="arrow-back"
              size="xl"
              variant="button-icon-color-white"
              shadow
              test={{
                dataTest: hash + '_previousButton',
              }}
              tracking={{
                dataTrack: dataTrack as string,
                dataTrackSection: hash,
                dataTrackText: 'Previous',
              }}
              css={navigationCss}
            />
            <ButtonIcon
              className={`button-navigation-next button-product-${hash}-next`}
              label="Next"
              icon="arrow-forward"
              size="xl"
              variant="button-icon-color-white"
              shadow
              test={{
                dataTest: hash + '_nextButton',
              }}
              tracking={{
                dataTrack: dataTrack as string,
                dataTrackSection: hash,
                dataTrackText: 'Next',
              }}
              css={navigationCss}
            />
          </>
        )}

        {!!remark && (
          <Container>
            <Text
              as="div"
              variant="text-white"
              wrap
              dangerouslySetInnerHTML={{
                __html: remark,
              }}
              data-test="content_item_remark"
              css={{ marginTop: '$space-6', fontSize: '80%' }}
            />
          </Container>
        )}

        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        {items?.length > 1 && (
          <Container>
            <PaginationBar
              variant={theme === 'dark' ? 'white' : 'red'}
              {...pagination}
              css={{
                ...pagination?.css,
                ...paginationCss,
              }}
            >
              <div
                className={`swiper-pagination swiper-pagination-product-${hash}`}
                data-test="paginations"
              />
            </PaginationBar>
          </Container>
        )}
      </StyledUspSlider>

      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      {!!TOGGLE_MODAL_TRANS.shouldMount && modalProduct.data?.index >= 0 && (
        <Modal
          type="product"
          open={modalProduct.isOpen}
          css={{
            opacity: TOGGLE_MODAL_TRANS.stage === 'enter' ? '1' : '0',
            transform:
              TOGGLE_MODAL_TRANS.stage === 'enter'
                ? 'translateZ(0) scale(1)'
                : 'translateZ(0) scale(0.9)',
            transition:
              'opacity 0.33s cubic-bezier(0.33, 1, 0.68, 1), transform 0.33s cubic-bezier(0.33, 1, 0.68, 1)',
          }}
          activeSlideIndex={modalProduct.data?.index as number}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          items={items?.map((o) => ({
            image: {
              src: o?.image?.src,
              alt: o?.image?.alt,
              width: o?.image?.width,
              height: o?.image?.height,
            },
            image2: {
              src: o?.image2?.src,
              alt: o?.image2?.alt,
              width: o?.image2?.width,
              height: o?.image2?.height,
            },
            title: o?.title,
            description: o?.description,
          }))}
          onClose={onCloseModal}
          dataTest="popup_gallery"
        />
      )}
    </>
  );
};

ProductUspSlider.displayName = 'ProductUspSlider';
